import { Bot } from "lucide-react";
import { visibleAssistantText } from "../../../../../packages/shared-types/src/index.js";
import type {
  MobileUiActionCallback,
  MobileUiActionKind,
  MobileUiInboundAction,
  SessionLiveRuntimeEvent,
} from "../../../../../packages/shared-types/src/index.js";
import type { SessionGetResponse } from "@/lib/api/generated/control-api.schemas";
import { Button } from "@/components/ui/button";
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from "@/components/ai/conversation";
import { isConversationIssueText } from "./conversation-status.js";

type ConversationActionReply = {
  label: string;
  replyText?: string;
  action?: MobileUiInboundAction;
};

type ConversationThreadMessage = SessionGetResponse["messages"][number];

export function ConversationMessageThread(params: {
  messages: ConversationThreadMessage[];
  runtimeEvents?: SessionLiveRuntimeEvent[];
  onActionReply?: (payload: ConversationActionReply) => void;
}) {
  return (
    <Conversation className="h-full min-h-[18rem]">
      <ConversationContent className="mx-auto w-full max-w-3xl gap-5 px-6 py-8">
        {!params.messages.length ? (
          <ConversationEmptyState
            icon={<Bot size={18} />}
            title="No messages in this session"
            description="Messages will appear here when the user talks to the agent."
          />
        ) : null}
        {params.messages.map((message) => {
        const isUser = message.role === "user";
        const isIssue = !isUser && isConversationIssueText(message.text);
        const contentBlocks = (message.content ?? [])
          .filter(isContentBlock)
          .filter((block) => shouldRenderContentBlock(message.role, block));
        const hasStructuredContent = contentBlocks.length > 0;
        const messageText = readDisplayText(message.role, message.text);
        return (
          <div
            key={message.messageId}
            className="flex"
            style={{ justifyContent: isUser ? "flex-end" : "flex-start" }}
          >
            <div
              className="max-w-[78%] rounded-2xl px-4 py-3"
              style={{
                background: isUser
                  ? "var(--accent)"
                  : isIssue
                    ? "color-mix(in srgb, var(--danger) 12%, var(--surface))"
                    : "var(--surface)",
                color: isUser ? "white" : "var(--text-strong)",
                border: isUser
                  ? undefined
                  : `1px solid ${
                      isIssue
                        ? "color-mix(in srgb, var(--danger) 35%, var(--border))"
                        : "var(--border)"
                    }`,
              }}
            >
              {isIssue ? (
                <div
                  className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: "var(--danger)" }}
                >
                  Runtime issue
                </div>
              ) : null}
              {hasStructuredContent ? (
                <div className="flex flex-col gap-2">
                  {contentBlocks.map((block, index) => (
                    <ContentBlockView
                      key={`${message.messageId}:${index}:${String(block.type)}`}
                      block={block}
                      role={message.role}
                      onActionReply={params.onActionReply}
                    />
                  ))}
                </div>
              ) : messageText ? (
                <div className="whitespace-pre-wrap text-sm leading-6">{messageText}</div>
              ) : (
                <div className="text-sm italic opacity-80">Non-text response</div>
              )}
              {!hasStructuredContent && message.mediaUrls?.length ? (
                <div className="mt-3 flex flex-col gap-1 text-xs opacity-90">
                  {message.mediaUrls.map((url) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      {url}
                    </a>
                  ))}
                </div>
              ) : null}
              <div className="mt-2 text-[11px] opacity-70">
                {new Date(message.createdAt).toLocaleString()}
              </div>
            </div>
          </div>
        );
        })}
        {params.runtimeEvents?.length ? <RuntimeLiveStatus events={params.runtimeEvents} /> : null}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
}

function RuntimeLiveStatus(params: { events: SessionLiveRuntimeEvent[] }) {
  const latestEvent = params.events.at(-1);
  if (!latestEvent) {
    return null;
  }
  const status = formatRuntimeStatus(latestEvent);
  if (!status) {
    return null;
  }
  const isTerminal = latestEvent.stream === "error" || isLifecycleTerminal(latestEvent);

  return (
    <div className="flex" style={{ justifyContent: "flex-start" }}>
      <div
        className="flex max-w-[78%] items-center gap-3 rounded-2xl border px-4 py-3"
        style={{
          background: isTerminal
            ? "color-mix(in srgb, var(--danger) 8%, var(--surface))"
            : "var(--surface)",
          color: "var(--text-strong)",
          borderColor: isTerminal
            ? "color-mix(in srgb, var(--danger) 35%, var(--border))"
            : "var(--border)",
        }}
      >
        <PulsingRuntimeGlyph danger={isTerminal} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm leading-6" style={{ color: "var(--text-strong)" }}>
            {status}
          </div>
          <div className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
            {new Date(latestEvent.ts).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
              second: "2-digit",
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function PulsingRuntimeGlyph(params: { danger: boolean }) {
  return (
    <span className="flex items-center gap-1" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-2 w-2 animate-pulse rounded-full"
          style={{
            background: params.danger ? "var(--danger)" : "var(--accent)",
            animationDelay: `${index * 160}ms`,
            opacity: 0.45 + index * 0.15,
          }}
        />
      ))}
    </span>
  );
}

function formatRuntimeStatus(event: SessionLiveRuntimeEvent): string | undefined {
  const data = isJsonRecord(event.data) ? event.data : {};

  if (event.stream === "thinking") {
    return readString(data, ["thinking", "text", "summary"]) || "Thinking";
  }

  if (event.stream === "item") {
    return [
      readString(data, ["progressText"]),
      readString(data, ["summary"]),
      readString(data, ["title", "name"]),
      readString(data, ["meta"]),
      readString(data, ["error"]),
    ].find(Boolean);
  }

  if (event.stream === "tool") {
    return readString(data, ["progressText", "summary", "title", "name", "error", "message"]);
  }

  if (event.stream === "plan") {
    return readString(data, ["title", "explanation"]) || "Updating the plan";
  }

  if (event.stream === "approval") {
    return (
      readString(data, ["message", "reason"]) ||
      readString(data, ["title"]) ||
      "Waiting for approval"
    );
  }

  if (event.stream === "command_output") {
    const title = readString(data, ["title", "name"]);
    if (title) {
      return `Running ${title}`;
    }
    const cwd = readString(data, ["cwd"]);
    return cwd ? `Running a command in ${cwd}` : "Running a command";
  }

  if (event.stream === "patch") {
    return readString(data, ["summary"]) || "Updating files";
  }

  if (event.stream === "assistant") {
    return readString(data, ["text", "delta"]) || "Writing the response";
  }

  if (event.stream === "lifecycle") {
    const phase = readString(data, ["phase"]);
    if (phase === "start") {
      return "Starting the run";
    }
    if (phase === "error") {
      return readString(data, ["message", "reason", "error"]) || "Run failed";
    }
    if (phase === "end") {
      return "Wrapping up";
    }
  }

  if (event.stream === "error") {
    return readString(data, ["message", "error", "reason"]) || "Runtime error";
  }

  return readString(data, ["message", "summary", "title"]);
}

function isLifecycleTerminal(event: SessionLiveRuntimeEvent): boolean {
  if (event.stream !== "lifecycle") {
    return false;
  }
  const data = isJsonRecord(event.data) ? event.data : {};
  const phase = readString(data, ["phase"]);
  return phase === "end" || phase === "error";
}

function ContentBlockView(params: {
  block: Record<string, unknown> & { type: string };
  role: string;
  onActionReply?: (payload: ConversationActionReply) => void;
}) {
  const type = String(params.block.type ?? "");
  if (type === "text") {
    const text = readDisplayText(
      params.role,
      typeof params.block.text === "string" ? params.block.text : "",
    );
    return <div className="whitespace-pre-wrap text-sm leading-6">{text}</div>;
  }

  if (type === "audio") {
    const source = isJsonRecord(params.block.source) ? params.block.source : null;
    const mediaType = typeof source?.media_type === "string" ? source.media_type : "audio/mpeg";
    const data = typeof source?.data === "string" ? source.data : "";
    if (!data) {
      return <div className="text-xs italic opacity-80">Audio block missing data.</div>;
    }
    const src = `data:${mediaType};base64,${data}`;
    return (
      <audio controls preload="none" style={{ width: "100%" }}>
        <source src={src} type={mediaType} />
      </audio>
    );
  }

  if (type === "image") {
    const mimeType =
      typeof params.block.mimeType === "string"
        ? params.block.mimeType
        : typeof params.block.media_type === "string"
          ? params.block.media_type
          : "image/png";
    const data = typeof params.block.data === "string" ? params.block.data : "";
    if (!data) {
      return <div className="text-xs italic opacity-80">Image block missing data.</div>;
    }
    const src = `data:${mimeType};base64,${data}`;
    return (
      <img
        src={src}
        alt="image"
        className="max-w-full rounded-md border"
        style={{ borderColor: "var(--border)" }}
      />
    );
  }

  if (type === "tool_result") {
    const result = typeof params.block.result === "string" ? params.block.result : "";
    return (
      <pre
        className="text-xs font-mono whitespace-pre-wrap rounded-md border p-3"
        style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
      >
        {result || "[tool result]"}
      </pre>
    );
  }

  if (type === "field_list") {
    const title = typeof params.block.title === "string" ? params.block.title : undefined;
    const fields = Array.isArray(params.block.fields) ? params.block.fields : [];
    return (
      <div
        className="rounded-md border p-3 text-sm"
        style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
      >
        {title ? <div className="mb-2 text-xs font-semibold uppercase tracking-wide">{title}</div> : null}
        <div className="grid gap-2">
          {fields.map((field, index) => {
            const record = isJsonRecord(field) ? field : {};
            const label = typeof record.label === "string" ? record.label : `Field ${index + 1}`;
            const value =
              typeof record.value === "string"
                ? record.value
                : typeof record.detail === "string"
                  ? record.detail
                  : typeof record.status === "string"
                    ? record.status
                    : "";
            return (
              <div key={`${label}:${index}`} className="grid gap-1">
                <div className="text-xs font-medium" style={{ color: "var(--muted-foreground)" }}>
                  {label}
                </div>
                <div className="whitespace-pre-wrap">{value || "\u2014"}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "confirmation") {
    const title = typeof params.block.title === "string" ? params.block.title : "Confirmation";
    const prompt = typeof params.block.prompt === "string" ? params.block.prompt : "";
    const actions = Array.isArray(params.block.actions) ? params.block.actions : [];
    return (
      <div
        className="rounded-md border p-3 text-sm"
        style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
      >
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide">{title}</div>
        {prompt ? <div className="mb-3 whitespace-pre-wrap">{prompt}</div> : null}
        {params.onActionReply ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((action, index) => {
              const record = isJsonRecord(action) ? action : {};
              const label = typeof record.label === "string" ? record.label : `Action ${index + 1}`;
              const replyText = typeof record.replyText === "string" ? record.replyText : "";
              const callbackAction = parseMobileInboundAction(record);
              const isEnabled = Boolean(replyText || callbackAction);
              return (
                <Button
                  key={`${label}:${index}`}
                  type="button"
                  variant={record.kind === "reject" ? "outline" : "default"}
                  size="sm"
                  disabled={!isEnabled}
                  onClick={() => {
                    if (!isEnabled) {
                      return;
                    }
                    params.onActionReply?.({
                      label,
                      ...(replyText ? { replyText } : {}),
                      ...(callbackAction ? { action: callbackAction } : {}),
                    });
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <pre
      className="text-xs font-mono whitespace-pre-wrap rounded-md border p-3"
      style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
    >
      {JSON.stringify(params.block, null, 2)}
    </pre>
  );
}

function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isContentBlock(value: unknown): value is Record<string, unknown> & { type: string } {
  return isJsonRecord(value) && typeof value.type === "string";
}

function shouldRenderContentBlock(
  role: string,
  block: Record<string, unknown> & { type: string },
): boolean {
  if (block.type !== "text") {
    return true;
  }
  return Boolean(readDisplayText(role, typeof block.text === "string" ? block.text : ""));
}

function readDisplayText(role: string, text: string | null | undefined): string {
  const trimmed = text?.trim();
  if (!trimmed) {
    return "";
  }
  return role === "assistant" ? visibleAssistantText(trimmed) : trimmed;
}

function readString(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return undefined;
}

function parseMobileInboundAction(record: Record<string, unknown>): MobileUiInboundAction | undefined {
  const callback = isJsonRecord(record.callback)
    ? (record.callback as unknown as MobileUiActionCallback)
    : undefined;
  if (!callback) {
    return undefined;
  }
  const actionId = callback.actionId?.trim();
  if (!actionId) {
    return undefined;
  }
  return {
    kind: "mobile.action",
    actionId,
    action: normalizeActionKind(record.kind),
    ...(callback.workflowKey ? { workflowKey: callback.workflowKey } : {}),
    ...(callback.executionId ? { executionId: callback.executionId } : {}),
    ...(callback.target ? { target: callback.target } : {}),
    ...(isJsonRecord(callback.payload) ? { payload: callback.payload } : {}),
  };
}

function normalizeActionKind(value: unknown): MobileUiActionKind {
  return value === "approve" ||
    value === "reject" ||
    value === "submit" ||
    value === "cancel" ||
    value === "open" ||
    value === "refresh" ||
    value === "custom"
    ? value
    : "custom";
}
